import { useState } from 'react';
import {
  familyAlertChannels,
  familyAlertChannelsSave,
  familyMyResidents,
  type ApiError,
  type FamilyAlertChannel,
  type FamilyAlertChannelsBody,
  type FamilyAlertChannelsResponse,
  type FamilyAlertUpdates,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { ErrLine, I, chip, pill, seg, sx, useApi, useGuarded } from './kit';

// How we alert you (family, Settings > My connections; Andrew 2026-10-12: "families should be
// able to get alerts in any way that they choose"). Per resident: push, text, WhatsApp and
// email, several allowed; messages from the care team and daily or weekly updates on top.
// Incidents and changes in condition always alert on the chosen channels. Choosing a text or
// WhatsApp with no consent on file shows the exact words to agree to; the server records them
// (GET/PUT /family/:residentId/alert-channels, v3/family/family-alerts.ts).

const NOTE = 'font-size:16px;color:#54606C;margin-top:4px;line-height:1.5;text-wrap:pretty;';
const ROW = 'display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;row-gap:8px;padding:10px 2px;border-top:1px solid rgba(34,39,46,.1);';

export function FamilyAlertChannels() {
  const mine = useApi((c, s) => familyMyResidents(c, { signal: s }), []);
  if (!mine.data) return mine.error ? <ErrLine error={mine.error} /> : <Skeleton rows={3} label="Loading how we alert you" />;
  const residents = mine.data.residents;
  if (!residents.length) return null;
  return (
    <div data-testid="family-alert-channels" style={sx('margin-top:14px;')}>
      {residents.map((r) => <ResidentAlerts key={r.id} residentId={r.id} />)}
    </div>
  );
}

function ResidentAlerts({ residentId }: { residentId: string }) {
  const { client } = useSession();
  const { wr } = useGuarded();
  const q = useApi((c, s) => familyAlertChannels(c, { params: { residentId }, signal: s }), [residentId]);
  const [ask, setAsk] = useState<{ channel: FamilyAlertChannel; wording: string; body: FamilyAlertChannelsBody } | null>(null);
  const [err, setErr] = useState<ApiError | null>(null);
  if (!q.data) return q.error ? <ErrLine error={q.error} /> : <Skeleton rows={4} label="Loading how we alert you" />;
  const d: FamilyAlertChannelsResponse = q.data;
  const who = d.residentFirst ?? 'your family member';

  const save = async (body: FamilyAlertChannelsBody) => {
    setErr(null);
    try {
      const next = await familyAlertChannelsSave(client, { params: { residentId }, body: { ...body, source: 'console' } });
      q.setData(() => next);
      setAsk(null);
    } catch (e) {
      const ae = e as ApiError;
      const b = ae.body as { needsConsent?: FamilyAlertChannel; wording?: string } | null;
      if (ae.status === 409 && b?.needsConsent && b.wording) { setAsk({ channel: b.needsConsent, wording: b.wording, body }); return; }
      setErr(ae);
    }
  };
  const base = (): FamilyAlertChannelsBody => ({ channels: d.choice.channels, careMessages: d.choice.careMessages, updates: d.choice.updates });
  const flip = (c: FamilyAlertChannel) => {
    const on = d.choice.channels.includes(c);
    const channels = on ? d.choice.channels.filter((x) => x !== c) : [...d.choice.channels, c];
    void save({ ...base(), channels, ...(on && c === 'whatsapp' ? { consent: { whatsapp: false } } : {}) });
  };

  return (
    <div data-testid={'family-alerts-' + residentId} style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:12px 2px;')}>
      <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I('How we alert you about ' + who)}</div>
      <div style={sx(NOTE)}>{I(d.always + ' Nothing is sent on a channel you have not turned on.')}</div>
      {!d.choice.channels.length && (
        <div role="status" style={sx('font-size:16px;color:#8A5A00;margin-top:6px;line-height:1.5;')}>
          {I('No channel is on, so the care team cannot alert you outside Aithora. They will call you instead.')}
        </div>
      )}
      {d.channels.map((c) => (
        <div key={c.channel} style={sx(ROW)} data-testid={'family-alert-' + c.channel}>
          <div style={sx('flex:1 1 260px;min-width:min(100%,260px);')}>
            <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;')}>
              <span style={sx('font-size:16px;font-weight:700;color:#22272E;')}>{I(c.label)}</span>
              {c.destination && <span style={sx('font-size:16px;color:#54606C;font-variant-numeric:tabular-nums;')}>{I(c.destination)}</span>}
              {c.chosen && c.ready && <span style={sx(chip('#12503C', 'rgba(31,107,82,.14)'))}>ON</span>}
              {c.chosen && !c.ready && <span style={sx(chip('#8A5A00', 'rgba(138,90,0,.12)'))}>ON, NOT SENDING</span>}
              {!c.canChoose && !c.chosen && <span style={sx(chip('#54606C', 'rgba(34,39,46,.08)'))}>UNAVAILABLE</span>}
            </div>
            {c.reason && <div style={sx(NOTE)}>{I(c.reason)}</div>}
            <div style={sx(NOTE)}>{I('It says: ' + c.says)}</div>
          </div>
          {/* A channel that cannot be chosen yet (no confirmed number, WhatsApp not set up) is
              shown unavailable with the server's reason, not as a switch that does nothing. */}
          {c.chosen || c.canChoose ? (
            <a
              href="#"
              role="switch"
              aria-checked={c.chosen}
              aria-label={c.label}
              {...wr(() => flip(c.channel))}
              style={sx(pill(c.chosen))}
            >
              {c.chosen ? 'On' : 'Off'}
            </a>
          ) : (
            <span
              role="switch"
              aria-checked={false}
              aria-disabled="true"
              aria-label={c.label}
              title={c.reason ?? 'Not available yet.'}
              data-testid={'family-alert-off-' + c.channel}
              style={sx(pill(false) + 'opacity:.55;cursor:not-allowed;')}
            >
              {'Unavailable'}
            </span>
          )}
        </div>
      ))}
      {ask && (
        <div role="dialog" aria-label="Agree first" data-testid="family-alert-consent" style={sx('margin-top:8px;padding:10px 12px;border-radius:10px;background:rgba(47,92,151,.07);')}>
          <div style={sx('font-size:16px;color:#22272E;line-height:1.5;text-wrap:pretty;')}>{I(ask.wording)}</div>
          <div style={sx('display:flex;gap:8px;margin-top:8px;flex-wrap:wrap;')}>
            <a href="#" {...wr(() => void save({ ...ask.body, consent: { ...(ask.body.consent ?? {}), [ask.channel]: true } }))} style={sx(pill(true))}>I agree</a>
            <a href="#" {...wr(() => setAsk(null))} style={sx(pill(false))}>Not now</a>
          </div>
        </div>
      )}
      <div style={sx(ROW)}>
        <div style={sx('flex:1 1 260px;min-width:min(100%,260px);')}>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Messages from the care team</div>
          <div style={sx(NOTE)}>{I('Also alert me when the care team sends a message about ' + who + '.')}</div>
        </div>
        <a href="#" role="switch" aria-checked={d.choice.careMessages} aria-label="Messages from the care team" {...wr(() => void save({ ...base(), careMessages: !d.choice.careMessages }))} style={sx(pill(d.choice.careMessages))}>
          {d.choice.careMessages ? 'On' : 'Off'}
        </a>
      </div>
      <div style={sx(ROW)}>
        <div style={sx('flex:1 1 260px;min-width:min(100%,260px);')}>
          <div style={sx('font-size:16px;font-weight:700;color:#22272E;')}>Updates</div>
          {d.updatesNote && <div style={sx(NOTE)}>{I(d.updatesNote)}</div>}
        </div>
        <span style={sx('display:inline-flex;gap:6px;flex-wrap:wrap;')}>
          {(['off', 'daily', 'weekly'] as FamilyAlertUpdates[]).map((u) => (
            <a key={u} href="#" aria-pressed={d.choice.updates === u} {...wr(() => void save({ ...base(), updates: u }))} style={sx(seg(d.choice.updates === u))}>
              {u === 'off' ? 'Off' : u === 'daily' ? 'Daily' : 'Weekly'}
            </a>
          ))}
        </span>
      </div>
      {d.quietHours.note && <div style={sx(NOTE)}>{I(d.quietHours.note)}</div>}
      <ErrLine error={err} />
    </div>
  );
}
